/**
 * Shared PDF utilities for YCK Court RN reports
 * Standardized header with RN logo, branding, and formatting
 */
import { RN_LOGO_BASE64 } from './logoBase64';
import { ORG_ADDRESS, ORG_PHONE, ORG_EMAIL_BOOKINGS } from '../constants';

/**
 * Adds the official YCK Court RN letterhead with logo to a PDF document
 * @param {jsPDF} doc - The jsPDF instance
 * @param {string} reportTitle - The title of the report
 * @param {string} reportSubtitle - Optional subtitle (e.g., month/year)
 * @param {object} options - Optional color settings { r, g, b } for accent color
 * @returns {number} The Y position after the header (to start content)
 */
export const addOfficialLetterhead = (doc, reportTitle, reportSubtitle = '', options = {}) => {
  const { r = 0, g = 51, b = 102 } = options; // Default: navy blue

  // Top accent bar
  doc.setFillColor(r, g, b);
  doc.rect(0, 0, 220, 5, 'F');

  // Logo - sized to match header text height
  try {
    doc.addImage(RN_LOGO_BASE64, 'PNG', 14, 8, 26, 26);
  } catch (e) {
    // Fallback: text badge if image fails
    doc.setFillColor(r, g, b);
    doc.circle(27, 21, 12, 'F');
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.text('YCK', 27, 19, { align: 'center' });
    doc.text('RN', 27, 25, { align: 'center' });
  }

  // Organization name (to the right of logo)
  doc.setFontSize(16);
  doc.setTextColor(r, g, b);
  doc.setFont("helvetica", "bold");
  doc.text("YIO CHU KANG COURT", 135, 14, { align: "center" });
  doc.text("RESIDENTS' NETWORK", 135, 21, { align: "center" });

  // Address
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(100, 100, 100);
  doc.text(ORG_ADDRESS, 135, 28, { align: "center" });
  doc.text(`Tel: ${ORG_PHONE} | Email: ${ORG_EMAIL_BOOKINGS}`, 135, 33, { align: "center" });

  // Divider line
  doc.setDrawColor(r, g, b);
  doc.setLineWidth(0.5);
  doc.line(15, 37, 195, 37);

  // Report title
  doc.setFontSize(14);
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "bold");
  doc.text(reportTitle, 105, 47, { align: "center" });

  let contentStartY = 55;
  if (reportSubtitle) {
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(reportSubtitle, 105, 54, { align: "center" });
    contentStartY = 62;
  }

  return contentStartY;
};

/**
 * Adds a standard footer to the current page
 * @param {jsPDF} doc - The jsPDF instance
 * @param {object} options - Optional { r, g, b } for accent color
 */
export const addReportFooter = (doc, options = {}) => {
  const { r = 0, g = 51, b = 102 } = options;
  const pageHeight = doc.internal.pageSize.height;

  doc.setDrawColor(r, g, b);
  doc.setLineWidth(0.3);
  doc.line(15, pageHeight - 25, 195, pageHeight - 25);

  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.setFont("helvetica", "italic");
  doc.text("This is a computer-generated document. No signature is required.", 105, pageHeight - 18, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.text(`Generated: ${new Date().toLocaleString('en-SG', { timeZone: 'Asia/Singapore' })} (Singapore Time)`, 105, pageHeight - 12, { align: "center" });
};

/**
 * Adds the older-style header (kept for backward compatibility)
 */
export const addReportHeader = (doc, reportTitle, reportSubtitle = '') => {
  return addOfficialLetterhead(doc, reportTitle, reportSubtitle);
};

/**
 * Format date to "01 Jan 2026" style
 */
export const formatPDFDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
};
